import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ErrorInterceptor } from './core/interceptors/error.interceptors';
import { provideRouter } from '@angular/router';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, withInterceptors, HttpClient, provideHttpClient } from '@angular/common/http';
import { loginRequest, msal_Config } from './auth-config';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { provideNgxMask } from 'ngx-mask';
import {
  MSAL_INSTANCE,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MsalService,
  MsalBroadcastService,
  MsalGuard
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType
} from '@azure/msal-browser';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { gzipRequestInterceptor } from './core/compression/gzip-request.interceptor';
import { IndexDBService } from './services/common/indexDB.service';
import { TabGuardService } from './services/common/tab-guard.service';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msal_Config);
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MsalGuardConfigurationFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) => new TranslateHttpLoader(http, './i18n/', '.json');

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideAppInitializer(() => (inject(MSAL_INSTANCE) as unknown as IPublicClientApplication).initialize()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigurationFactory,
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([gzipRequestInterceptor, loadingInterceptor, jwtInterceptor]),
      withInterceptorsFromDi()
    ),
    provideAppInitializer(() => {
      // Fire-and-forget: sweep orphaned IndexedDBs in the background without blocking app startup.
      // If several orphaned DBs exist, each deletion has a 3-second timeout — running non-blocking
      // avoids delaying the initial render.
      inject(IndexDBService).sweepOrphanedDatabases();
    }),
    provideAppInitializer(() => { inject(TabGuardService).initialize(); }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    provideNgxMask(),
    importProvidersFrom(
      NgxSpinnerModule.forRoot(),
      ToastrModule.forRoot()
    ),
    DatePipe,
    CurrencyPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    }
  ]
};
